import { useEffect, useMemo, useState } from "react";
import { Circle, CircleCheck, Loader2, Search, X } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../context/LanguageContext";
import { useStoreSlotPlan } from "../hooks/useStoreSlotPlan";
import { getStoreActivity } from "../services/StoreActivityService";
import { geocodeZipCode } from "../utils/geocoding";
import { getStoreChangeWarning, parseStoreChangeError } from "../utils/userUtils";
import {
  calculateDistance,
  formatStoreIdsForSave,
  normalizeStore,
  storeActivityLabel,
  type Store,
  type StoreWithDistance,
} from "./filtertypes/StoresFilter.utils";

const NEARBY_LIMIT = 50;

interface SheetStoreEditorProps {
  /** Which of the retailer's store slots is being picked (an index past the saved stores adds one). */
  slotIndex: number;
  /** The retailer's store catalog, already loaded by the filters sheet. */
  stores: unknown[] | null | undefined;
  storesLoading?: boolean;
  /** Runs after a successful save. */
  onDone: () => void;
}

/**
 * Pick the store for one slot of the current retailer, inside the filters
 * sheet (the sheet shows a back + "Choose a store" header around it): nearby
 * stores (or a search), each with its deal activity. Picking a store saves
 * right away. Same rules as the Change Stores page: the Basic change-limit
 * confirmation and the server's change-limit errors. Mirrors
 * pinto/components/SheetStoreEditor.tsx.
 */
export default function SheetStoreEditor({ slotIndex, stores, storesLoading = false, onDone }: SheetStoreEditorProps) {
  const { t, currentLanguage } = useTranslation();
  const dateLocale = currentLanguage === "es" ? "es-US" : "en-US";
  const { user, retailer, retailerName, selectedStores, storeLimit, saveStores } = useStoreSlotPlan();

  const [query, setQuery] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [nearby, setNearby] = useState<StoreWithDistance[] | null>(null);
  const [activity, setActivity] = useState<Record<string, boolean>>({});
  const [pending, setPending] = useState<{ storeId: string; nextStores: string[]; date: string } | null>(null);
  const [saveError, setSaveError] = useState(false);

  const catalog = useMemo(
    () => ((stores as Store[] | null | undefined) || []).map((store) => normalizeStore(store)).filter((store) => store.normalizedId),
    [stores]
  );
  const slots = useMemo(
    () => Array.from({ length: Math.max(storeLimit, selectedStores.length, slotIndex + 1) }, (_, index) => selectedStores[index] || ""),
    [selectedStores, slotIndex, storeLimit]
  );
  const byId = useMemo(() => {
    const map = new Map<string, StoreWithDistance>();
    for (const store of catalog) map.set(store.normalizedId, store);
    for (const store of nearby || []) map.set(store.normalizedId, store);
    return map;
  }, [catalog, nearby]);

  // Nearest stores to the saved ZIP; the start of the catalog when the ZIP can't be placed.
  useEffect(() => {
    if (storesLoading) return;
    let cancelled = false;
    void (async () => {
      const zip = (user?.zipCode || "").trim();
      const coords = /^\d{5}$/.test(zip) ? await geocodeZipCode(zip).catch(() => null) : null;
      if (cancelled) return;
      setNearby(
        coords
          ? catalog
              .filter((store) => store.normalizedGeo && Number.isFinite(store.normalizedGeo.lat) && Number.isFinite(store.normalizedGeo.long))
              .map((store) => ({ ...store, distance: calculateDistance(coords.lat, coords.long, store.normalizedGeo!.lat, store.normalizedGeo!.long) }))
              .filter((store) => store.distance <= 100)
              .sort((a, b) => a.distance - b.distance)
              .slice(0, NEARBY_LIMIT)
          : catalog.slice(0, NEARBY_LIMIT)
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [catalog, storesLoading, user?.zipCode]);

  const pickerStores = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return nearby || [];
    return catalog
      .filter((store) =>
        store.normalizedId.toLowerCase().includes(q) ||
        store.normalizedAddress.toLowerCase().includes(q) ||
        store.normalizedPostalCode.toLowerCase().startsWith(q)
      )
      .map((store) => byId.get(store.normalizedId) || store)
      .slice(0, NEARBY_LIMIT);
  }, [byId, catalog, nearby, query]);

  const activityKey = useMemo(() => {
    const ids = new Set(slots.filter(Boolean));
    for (const store of pickerStores) {
      if (ids.size >= 100) break;
      ids.add(store.normalizedId);
    }
    return [...ids].join("|");
  }, [pickerStores, slots]);

  useEffect(() => {
    const ids = activityKey ? activityKey.split("|") : [];
    if (ids.length === 0) return;
    let cancelled = false;
    getStoreActivity(retailer, ids)
      .then((result) => {
        if (!cancelled) setActivity((previous) => ({ ...previous, ...result }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activityKey, retailer]);

  const persist = async (storeId: string, nextStores: string[]) => {
    setPending(null);
    setSaveError(false);
    setSavingId(storeId);
    try {
      await saveStores(nextStores);
      setSavingId(null);
      onDone();
    } catch (error: unknown) {
      setSavingId(null);
      const message = parseStoreChangeError(error);
      if (message) {
        toast.warn(`${t("storeEdit.storeChangeLimit")}: ${message}`, { position: "top-right", autoClose: 8000 });
      } else {
        setSaveError(true);
      }
    }
  };

  const choose = (storeId: string) => {
    if (savingId) return;
    const next = [...slots];
    next[slotIndex] = storeId;
    const nextStores = formatStoreIdsForSave(next, retailer, (stores as Store[] | null | undefined) ?? null);
    const warning = getStoreChangeWarning(user, retailer, Date.now(), nextStores);
    if (!warning) {
      void persist(storeId, nextStores);
      return;
    }
    const date = warning.nextAllowedDate.toLocaleString(dateLocale, { month: "long", day: "numeric", hour: "numeric", minute: "2-digit" });
    setPending({ storeId, nextStores, date });
  };

  // Known activity only: deals there now, or a store whose deals land within 24 hours.
  const statusOf = (storeId: string): boolean | undefined =>
    Object.prototype.hasOwnProperty.call(activity, storeId) ? activity[storeId] : undefined;

  const current = slots[slotIndex];
  return (
    <div className="px-5 py-3">
      <div className="relative mb-2">
        <Search size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-400" />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("storeEdit.search")}
          aria-label={t("storeEdit.search")}
          autoComplete="off"
          className="block h-11 w-full rounded-2xl border border-transparent bg-gray-100/80 pl-10 pr-9 text-[15px] text-gray-900 placeholder-gray-400 transition-all focus:border-primary-500/30 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/15 dark:bg-dark-800 dark:text-white dark:placeholder-dark-500 dark:focus:bg-dark-700 [&::-webkit-search-cancel-button]:hidden"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label={t("common.clear")}
            className="absolute right-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 hover:bg-gray-200/70 hover:text-gray-600 dark:text-dark-500 dark:hover:bg-dark-700 dark:hover:text-dark-300"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {pending && (
        <div role="alertdialog" aria-labelledby="sheet-store-confirm-title" className="mb-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/40 dark:bg-amber-950/30">
          <p id="sheet-store-confirm-title" className="text-sm font-semibold text-amber-900 dark:text-amber-100">
            {t("storeEdit.confirmTitle", { retailer: retailerName })}
          </p>
          <p className="mt-1 text-[13px] leading-[18px] text-amber-800 dark:text-amber-200">
            {t("storeEdit.confirmBody", { date: pending.date })}
          </p>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setPending(null)} className="min-h-9 rounded-lg px-3 text-sm font-medium text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/30">
              {t("storeEdit.cancel")}
            </button>
            <button type="button" onClick={() => void persist(pending.storeId, pending.nextStores)} className="min-h-9 rounded-lg bg-primary-600 px-3 text-sm font-semibold text-white hover:bg-primary-700">
              {t("storeEdit.confirm")}
            </button>
          </div>
        </div>
      )}

      {saveError && (
        <p role="alert" className="mb-2 rounded-xl bg-red-50 px-4 py-3 text-[13px] text-red-700 dark:bg-red-950/30 dark:text-red-300">
          <span className="font-semibold">{t("storeEdit.changeFailed")}.</span> {t("storeEdit.changeFailedBody")}
        </p>
      )}

      {nearby === null ? (
        <div role="status" aria-label={t("common.loading")} className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-purple-300" />
        </div>
      ) : pickerStores.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500 dark:text-dark-300">{t("storeEdit.none")}</p>
      ) : (
        <div role="radiogroup" aria-label={t("storeEdit.choose")} className="divide-y divide-gray-200/80 dark:divide-dark-700">
          {pickerStores.map((store) => {
            const id = store.normalizedId;
            const isCurrent = id === current;
            const isTaken = !isCurrent && slots.includes(id);
            const [street, ...rest] = store.normalizedAddress.split(",").map((part) => part.trim());
            const city = rest.filter(Boolean)[0];
            const status = statusOf(id);
            const statusLabel = storeActivityLabel(status, t);
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={isCurrent}
                aria-label={`#${id}, ${store.normalizedAddress}`}
                disabled={isCurrent || isTaken || !!savingId}
                onClick={() => choose(id)}
                className={`flex min-h-[58px] w-full items-center gap-3 py-2.5 text-left transition-colors enabled:hover:bg-gray-50 dark:enabled:hover:bg-dark-800/60 ${isTaken ? "opacity-50" : ""} ${pending?.storeId === id ? "bg-amber-50/60 dark:bg-amber-950/20" : ""}`}
              >
                <span className="flex w-6 shrink-0 items-center justify-center" aria-hidden="true">
                  {savingId === id ? (
                    <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-purple-300" />
                  ) : isCurrent || isTaken ? (
                    <CircleCheck size={23} strokeWidth={1.75} className={isCurrent ? "fill-primary-600 text-primary-600 [&>path]:stroke-white" : "text-gray-400 dark:text-dark-400"} />
                  ) : (
                    <Circle size={23} strokeWidth={1.25} className="text-gray-400 dark:text-dark-400" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-bold leading-5 text-gray-900 dark:text-white">{city || street}</span>
                  <span className="mt-0.5 block truncate text-[13px] leading-[17px] text-gray-500 dark:text-dark-300">{city ? `#${id} · ${street}` : `#${id}`}</span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  {store.distance != null && (
                    <span className="text-[13px] font-bold tabular-nums text-gray-900 dark:text-white">{store.distance.toFixed(1)} mi</span>
                  )}
                  {statusLabel && (
                    <span className={`flex items-center gap-1.5 text-xs font-bold ${status ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                      <span className={`h-[7px] w-[7px] rounded-full ${status ? "bg-emerald-600 dark:bg-emerald-400" : "bg-amber-600 dark:bg-amber-400"}`} />
                      {statusLabel}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
